import React, { useEffect, useMemo, useState } from "react";
import get from "lodash/get";
import { Header } from "@tvg/design-system/web";
import tvgConf from "@tvg/conf";
import StoryblokClient from "storyblok-js-client";
import { useLocation, useNavigate } from "react-router-dom";
import {
  RacingFAQ,
  RichTextSection,
  BetTypesSection,
  ArticlePageLoading,
  Footer,
  SeoHeader
} from "../components";
import useContentData from "../hooks/useContentData";
import {
  Container,
  Title,
  Columns,
  MainContent,
  SideContent
} from "./styled-components";
import { getSectionByType } from "../utils/storyblok";
import { StoryblokPageContent } from "../types";
import { HORSE_RACING_101_URL } from "../constants";

interface ArticleProps {
  slug: string;
}

const storyblokToken = tvgConf().config("storyblokToken");
const storyblokClient = new StoryblokClient({
  accessToken: storyblokToken,
  cache: {
    clear: "auto",
    type: "memory"
  }
});

export const Article = ({ slug }: ArticleProps) => {
  const { pathname, hash } = useLocation();
  const navigate = useNavigate();

  const [isBetTypesLoading, setBetTypesLoading] = useState(
    pathname === `${HORSE_RACING_101_URL}/betting`
  );

  const { contentData, isLoading, isError } =
    useContentData<StoryblokPageContent>(slug);

  const faqSection = useMemo(
    () => getSectionByType(contentData?.sections, "faq"),
    [contentData]
  );

  const richTextSection = useMemo(
    () => getSectionByType(contentData?.sections, "richtext"),
    [contentData]
  );

  const betTypesSection = useMemo(
    () => getSectionByType(contentData?.sections, "bet_types"),
    [contentData]
  );

  const footerSection = useMemo(() => {
    const footerTitle = contentData?.footer_title;
    const footerLink = contentData?.footer_link;

    return (
      footerTitle &&
      footerLink?.cached_url && (
        <Footer footerTitle={footerTitle} footerLink={footerLink} />
      )
    );
  }, [contentData]);

  const shouldHideFaqAndRichText = useMemo(
    () =>
      hash &&
      betTypesSection?.content.some(
        (item) => get(item, "hash") === hash.replace("#", "")
      ),
    [betTypesSection, hash]
  );

  useEffect(() => {
    if (isError) {
      navigate(HORSE_RACING_101_URL);
    }
  }, [isError]);

  useEffect(() => {
    if (betTypesSection) {
      setBetTypesLoading(false);
    }
  }, [betTypesSection]);

  if (isLoading || isBetTypesLoading)
    return <ArticlePageLoading isBetting={isBetTypesLoading} />;

  return (
    <Container data-qa-label="education-hub-container">
      {contentData?.seo_meta_data && (
        <SeoHeader seoMetaData={contentData.seo_meta_data} />
      )}
      <Title>
        <Header
          tag="h1"
          fontFamily="bold"
          qaLabel="education-hub-title"
          color="--fd-colors-content-strong"
        >
          {get(contentData, "title", "")}
        </Header>
      </Title>
      <Columns>
        <MainContent data-qa-label="education-hub-main-content">
          {betTypesSection && (
            <BetTypesSection
              data={betTypesSection}
              storyblokClient={storyblokClient}
              footerTitle={contentData?.footer_title}
            />
          )}

          {!shouldHideFaqAndRichText && richTextSection && (
            <RichTextSection
              data={richTextSection}
              storyblokClient={storyblokClient}
              hasSectionsMargin={!!betTypesSection}
              footer={footerSection}
            />
          )}
        </MainContent>

        {!shouldHideFaqAndRichText && faqSection && (
          <SideContent data-qa-label="education-hub-side-content">
            <RacingFAQ data={faqSection} storyblokClient={storyblokClient} />
          </SideContent>
        )}
      </Columns>
    </Container>
  );
};
